import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import { IconAlert } from '@/components/icons/StatusIcons';

const DEVICE_DISPLAY_NAMES: Record<DeviceType, string> = {
  [DeviceType.LITE]: 'BELL LITE',
  [DeviceType.MINI]: 'BELL MINI',
};

interface DeviceMismatchBannerProps {
  /** Currently connected device type */
  detectedType: DeviceType;
  /** Device type this page expects */
  expectedType: DeviceType;
}

/**
 * Full-width warning banner shown when the connected device type doesn't
 * match the configuration page. Styled to match the project's Sonner
 * warning toast (GlobalStyles.tsx).
 *
 * Usage:
 * ```tsx
 * {isDeviceConnected && detectedDeviceType && !isCompatible && (
 *   <DeviceMismatchBanner
 *     detectedType={detectedDeviceType}
 *     expectedType={EXPECTED_DEVICE_TYPE}
 *   />
 * )}
 * ```
 */
export const DeviceMismatchBanner: React.FC<DeviceMismatchBannerProps> = ({
  detectedType,
  expectedType,
}) => {
  const { t } = useTranslation('config');

  const connectedName = DEVICE_DISPLAY_NAMES[detectedType] ?? t('msg_unknownDevice');
  const expectedName = DEVICE_DISPLAY_NAMES[expectedType] ?? t('msg_unknownDevice');

  return (
    <Banner>
      <IconWrapper>
        <IconAlert width={16} height={16} />
      </IconWrapper>
      {t('msg_connectCorrectDevice', { connected: connectedName, expected: expectedName })}
    </Banner>
  );
};

const Banner = styled.div`
  padding: ${({ theme }) => theme.spacing[5]}px ${({ theme }) => theme.spacing[6]}px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px solid ${({ theme }) => theme.colors.status.caution.stroke.border};
  color: ${({ theme }) => theme.colors.status.caution.stroke.text};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 120%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[5]}px;
  width: 100%;
`;

const IconWrapper = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.status.caution.stroke.icon};
`;
